import { useEffect, useRef, useState } from "react";
import ApiAuth from "../modules/api/auth";
import AppUser from "../modules/app-user/app-user";

const INTERVAL_REFRESH_TOKEN_MS = 30 * 60 * 1000;
const INTERVAL_CHECK_USER_MS = 1000;
const REFRESH_LOCK_KEY = "token_refresh_lock";
const REFRESH_LOCK_TTL = 10000;
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const DEBUG_TOKEN_REFRESH = localStorage.getItem("DEBUG_TOKEN_REFRESH") === "true";

function log(...args: any[]) {
  if (DEBUG_TOKEN_REFRESH) {
    console.log(`[TokenRefresh][${TAB_ID}]`, ...args);
  }
}

function tryAcquireLock(): boolean {
  const now = Date.now();
  const lockData = localStorage.getItem(REFRESH_LOCK_KEY);
  
  if (lockData) {
    try {
      const { tabId, expiry } = JSON.parse(lockData);
      if (expiry > now && tabId !== TAB_ID) {
        log("Lock held by", tabId, "expires in", expiry - now, "ms");
        return false;
      }
    } catch {}
  }
  
  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ tabId: TAB_ID, expiry: now + REFRESH_LOCK_TTL }));
  log("Lock acquired");
  return true;
}

function releaseLock() {
  const lockData = localStorage.getItem(REFRESH_LOCK_KEY);
  if (lockData) {
    try {
      const { tabId } = JSON.parse(lockData);
      if (tabId === TAB_ID) {
        localStorage.removeItem(REFRESH_LOCK_KEY);
        log("Lock released");
      }
    } catch {}
  }
}

function notifyTokensUpdated() {
  localStorage.setItem("token_updated_at", Date.now().toString());
  log("Notified tokens updated");
}

export default function useRefreshTokenUser() {
  const [isValid, setIsValid] = useState<boolean>(undefined);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const userRef = useRef<{[key: string]: any}>({
    checked: false,
    lastTokens: { token: "", refreshToken: "" }
  });

  useEffect(() => {
    let timers: any;
    let refresh = async (isRetry = false) => {
      const user = await AppUser.getInfo();
      const jitter = Math.random() * 30000 - 15000;
      const retry = () => {
        timers = setTimeout(() => refresh(), INTERVAL_REFRESH_TOKEN_MS + jitter);
      };

      if (user.logged) {
        userRef.current.checked = true;

        if (!tryAcquireLock()) {
          log("Waiting for other tab to refresh");
          await new Promise(resolve => setTimeout(resolve, 2000));
          const updatedUser = await AppUser.getInfo();
          if (updatedUser.token !== user.token) {
            log("Tokens updated by another tab");
            userRef.current.lastTokens = { token: updatedUser.token, refreshToken: updatedUser.refreshToken };
            setIsNetworkError(false);
            setIsValid(true);
            retry();
            return;
          }
        }

        log("Refreshing token", { isRetry });
        const res = await ApiAuth.refreshToken(user.token, user.refreshToken);
        releaseLock();

        if (res.status) {
          log("Refresh success");
          await AppUser.setInfo({
            token: res.data.authToken,
            refreshToken: res.data.refreshToken
          });
          userRef.current.lastTokens = { token: res.data.authToken, refreshToken: res.data.refreshToken };
          notifyTokensUpdated();
          setIsNetworkError(false);
          setIsValid(true);
          retry();
          return;
        }

        if (res.status === false && res.networkError) {
          log("Network error, retrying");
          setIsNetworkError(true);
          setIsValid(true);
          retry();
          return;
        }

        if (!isRetry) {
          log("Refresh failed, checking if tokens changed");
          await new Promise(resolve => setTimeout(resolve, 500));
          const latestUser = await AppUser.getInfo();
          if (latestUser.token !== user.token || latestUser.refreshToken !== user.refreshToken) {
            log("Tokens changed, retrying with new tokens");
            return refresh(true);
          }
        }

        log("Refresh failed definitively");
        setIsNetworkError(false);
        setIsValid(false);
      } else {
        setIsValid(false);
      }
    };

    let timersUser: any;
    let userChecker = async () => {
      const user = await AppUser.getInfo();

      if (!user.logged) {
        userRef.current.checked = false;
        setIsValid(false);
      }

      if (user.logged && !userRef.current.checked) {
        userRef.current.checked = true;
        clearTimeout(timers);
        refresh();
      }

      timersUser = setTimeout(() => userChecker(), INTERVAL_CHECK_USER_MS);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token_updated_at" && e.newValue) {
        log("Detected token update from another tab");
        AppUser.getInfo().then(user => {
          if (user.logged) {
            userRef.current.lastTokens = { token: user.token, refreshToken: user.refreshToken };
            setIsValid(true);
            setIsNetworkError(false);
          }
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);

    refresh().then(() => {
      setInitialized(true);
      userChecker();
    });

    return () => {
      clearTimeout(timersUser);
      clearTimeout(timers);
      releaseLock();
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return { isValid, isNetworkError, initialized };
}
